import * as React from 'react';

import { Text, makeUclComponent } from '@rbilabs/universal-components';
import { StyleSheet, View } from 'react-native';

import LoadingAnimation from 'components/loading-animation';
import { t } from 'state/intl/translations';
import { ShortCodeState } from 'state/loyalty/in-restaurant-redemption';
import { primitive } from 'styles/constants/primitives';

interface IWhopperShortCodeWidgetProps {
  shortCode?: string;
  shortCodeState: ShortCodeState;
  isShortCodeLoading: boolean;
}

const WhopperShortCodeWidget: React.FC<IWhopperShortCodeWidgetProps> = ({
  shortCode,
  shortCodeState,
  isShortCodeLoading,
}) => {
  // Format code with spaces between every 2 digits: "123456" -> "12 34 56"
  const formatCodeWithSpaces = (code: string) => {
    if (!code || code.length !== 6) {
      return '';
    }
    return `${code.slice(0, 2)} ${code.slice(2, 4)} ${code.slice(4, 6)}`;
  };

  const formattedCode = formatCodeWithSpaces(shortCode || '');

  return (
    <View style={styles.container}>
      <Text style={styles.title} accessibilityLabel={t('whopperCodeTitle')}>
        {t('whopperCodeTitle')}
      </Text>
      <Text style={styles.subtitle} accessibilityLabel={t('whopperCodeSubtitle')}>
        {t('whopperCodeSubtitle')}
      </Text>

      {([ShortCodeState.Pending, ShortCodeState.Claimed].includes(shortCodeState) ||
        (shortCodeState === ShortCodeState.None && isShortCodeLoading)) && (
        <View style={styles.codeContainer} testID="whopper-short-code">
          {isShortCodeLoading ? (
            <LoadingAnimation />
          ) : (
            <Text style={styles.codeText} accessibilityLabel={formattedCode} numberOfLines={1}>
              {formattedCode}
            </Text>
          )}
        </View>
      )}

      {shortCodeState === ShortCodeState.Expired && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText} accessibilityLabel={t('whopperCodeExpired')}>
            {t('whopperCodeExpired')}
          </Text>
        </View>
      )}

      {shortCodeState === ShortCodeState.RequestError && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText} accessibilityLabel={t('whopperCodeErrorMessage')}>
            {t('whopperCodeErrorMessage')}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#F5A623',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingTop: 40,
    paddingBottom: 20,
    paddingHorizontal: 24,
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
    color: primitive.bk.$bbqBrown,
    fontFamily: 'Flame',
  },
  subtitle: {
    fontSize: 14,
    fontWeight: '400',
    marginBottom: 40,
    textAlign: 'center',
    color: primitive.bk.$bbqBrown,
    fontFamily: 'Flame Sans',
  },
  codeContainer: {
    paddingVertical: 16,
    paddingHorizontal: 32,
    backgroundColor: 'transparent',
    minHeight: 80,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  codeText: {
    fontSize: 42,
    fontWeight: 'bold',
    letterSpacing: 8,
    color: primitive.bk.$bbqBrown,
    fontFamily: 'Flame-Bold',
    lineHeight: 56,
    textAlign: 'center',
    flexWrap: 'nowrap',
  },
  errorContainer: {
    padding: 20,
    backgroundColor: primitive.bk.$errorLight,
    borderRadius: 8,
  },
  errorText: {
    color: primitive.bk.$alertRed,
    textAlign: 'center',
    fontFamily: 'Flame Sans',
  },
});

export default makeUclComponent(WhopperShortCodeWidget);
